import { useState, useMemo, useRef, useCallback } from "react";
import { PhotoLightbox, useLightbox } from "@/components/photo-lightbox";
import AdminLayout from "@/components/layout-admin";
import { useApartments, useAdminUsers } from "@/hooks/use-resources";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { DeleteConfirmDialog } from "@/components/ui/delete-confirm-dialog";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Wrench, Plus, Clock, CheckCircle2, AlertCircle, PlayCircle, AlertTriangle,
  Minus, Home, User, Trash2, Calendar, ChevronLeft, ChevronRight, List, CalendarDays,
  DollarSign, Building2, Camera, ImageIcon, X, Pencil, FileText, Send, Download, Shield, ExternalLink, MapPin, Copy,
} from "lucide-react";
import { format } from "date-fns";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { Apartment } from "@shared/routes";

type FilterStatus = "all" | "pending" | "pending_approval" | "approved" | "assigned" | "in_progress" | "completed" | "cancelled";
type ViewMode = "calendar" | "list";
const priorityOrder: Record<string, number> = { urgent: 0, high: 1, medium: 2, low: 3 };

function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export default function AdminMaintenance() {
  const { data: jobs = [], isLoading } = useQuery<any[]>({ queryKey: ["/api/admin/maintenance-jobs"] });
  const { data: apartments = [] } = useApartments();
  const { data: users = [] } = useAdminUsers();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [filter, setFilter] = useState<FilterStatus>("all");
  const [viewMode, setViewMode] = useState<ViewMode>("calendar");
  const [calendarOffset, setCalendarOffset] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<any>(null);
  const [maintSheetOpen, setMaintSheetOpen] = useState(false);
  const activeMaintJob = useMemo(() => {
    if (!selectedJob) return null;
    return (jobs as any[]).find((j: any) => j.id === selectedJob.id) || selectedJob;
  }, [selectedJob, jobs]);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [photoPhase, setPhotoPhase] = useState<"before" | "after">("before");
  const [photoCaption, setPhotoCaption] = useState("");
  const { lightboxState, openLightbox, closeLightbox } = useLightbox();
  const [form, setForm] = useState({
    title: "", description: "", priority: "medium", apartmentId: "",
    ownerUserId: "none", assignedToUserId: "none", scheduledDate: "",
    isBillable: false, costAmount: "", costDescription: "",
  });

  const [dragOverCell, setDragOverCell] = useState<string | null>(null);
  const [editingDetail, setEditingDetail] = useState(false);
  const [deleteConfirmMaintJob, setDeleteConfirmMaintJob] = useState<any | null>(null);
  const [deleteConfirmMaintPhoto, setDeleteConfirmMaintPhoto] = useState<{ jobId: number; photoId: number; phase: "before" | "after" } | null>(null);
  const [detailForm, setDetailForm] = useState({
    title: "", description: "", scheduledDate: "", apartmentId: "",
    ownerUserId: "", isBillable: false, costAmount: "", costDescription: "",
  });

  const maintenanceUsers = (users as any[]).filter(u => u.role === "maintenance" || ((u.additionalRoles as string[] | undefined) ?? []).includes("maintenance"));
  const ownerUsers = (users as any[]).filter(u => u.role === "owner");
  const typedApts = apartments as Apartment[];

  const getApartmentName = (id: number | null) => {
    if (!id) return "No apartment";
    return typedApts.find(a => a.id === id)?.name || `Apt #${id}`;
  };
  const getAssigneeName = (id: number | null) => {
    if (!id) return "Unassigned";
    const u = (users as any[]).find(u => u.id === id);
    return u?.fullName || u?.username || `User #${id}`;
  };

  const filtered = (jobs as any[])
    .filter(j => filter === "all" || j.status === filter)
    .sort((a, b) => (priorityOrder[a.priority || "medium"] ?? 2) - (priorityOrder[b.priority || "medium"] ?? 2));

  const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline"; icon: any; color: string }> = {
    pending: { label: "Pending", variant: "destructive", icon: AlertCircle, color: "bg-red-500/80" },
    pending_approval: { label: "Awaiting Approval", variant: "destructive", icon: Clock, color: "bg-orange-500/80" },
    approved: { label: "Approved", variant: "default", icon: CheckCircle2, color: "bg-emerald-500/80" },
    assigned: { label: "Assigned", variant: "outline", icon: Clock, color: "bg-amber-500/80" },
    in_progress: { label: "In Progress", variant: "default", icon: PlayCircle, color: "bg-blue-500/80" },
    completed: { label: "Completed", variant: "secondary", icon: CheckCircle2, color: "bg-green-500/80" },
    cancelled: { label: "Cancelled", variant: "outline", icon: Minus, color: "bg-gray-400/80" },
  };

  const priorityConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive"; icon: any }> = {
    urgent: { label: "Urgent", variant: "destructive", icon: AlertTriangle },
    high: { label: "High", variant: "destructive", icon: AlertTriangle },
    medium: { label: "Medium", variant: "default", icon: Clock },
    low: { label: "Low", variant: "secondary", icon: Minus },
  };

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest("POST", "/api/admin/maintenance-jobs", data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/maintenance-jobs"] });
      setCreateOpen(false);
      setForm({ title: "", description: "", priority: "medium", apartmentId: "", ownerUserId: "none", assignedToUserId: "none", scheduledDate: "", isBillable: false, costAmount: "", costDescription: "" });
      toast({ title: "Maintenance job created" });
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const autoScheduleMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/maintenance-jobs/auto-schedule");
      return res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/maintenance-jobs"] });
      toast({ title: "Auto-scheduled", description: data.message || "Jobs scheduled based on unit availability" });
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const res = await apiRequest("PATCH", `/api/admin/maintenance-jobs/${id}`, data);
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/maintenance-jobs"] });
      setSelectedJob(data);
      toast({ title: "Job updated" });
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const handleMaintDragStart = useCallback((e: React.DragEvent, jobId: number) => {
    e.dataTransfer.setData("text/plain", String(jobId));
    e.dataTransfer.effectAllowed = "move";
  }, []);

  const handleMaintDrop = useCallback((e: React.DragEvent, day: Date) => {
    e.preventDefault();
    setDragOverCell(null);
    const jobId = Number(e.dataTransfer.getData("text/plain"));
    if (!jobId) return;
    const dateStr = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}T12:00:00.000Z`;
    updateMutation.mutate({ id: jobId, data: { scheduledDate: dateStr } });
  }, [updateMutation]);

  const startEditDetail = useCallback(() => {
    if (!selectedJob) return;
    const sd = selectedJob.scheduledDate;
    const dateVal = sd ? (typeof sd === 'string' && /^\d{4}-\d{2}-\d{2}/.test(sd) ? sd.slice(0, 10) : new Date(sd).toISOString().split('T')[0]) : "";
    setDetailForm({
      title: selectedJob.title || "",
      description: selectedJob.description || "",
      scheduledDate: dateVal,
      apartmentId: selectedJob.apartmentId ? String(selectedJob.apartmentId) : "",
      ownerUserId: selectedJob.ownerUserId ? String(selectedJob.ownerUserId) : "",
      isBillable: selectedJob.isBillable || false,
      costAmount: selectedJob.costAmount || "",
      costDescription: selectedJob.costDescription || "",
    });
    setEditingDetail(true);
  }, [selectedJob]);

  const saveEditDetail = useCallback(() => {
    if (!selectedJob) return;
    const updates: any = {};
    if (detailForm.title !== selectedJob.title) updates.title = detailForm.title;
    if (detailForm.description !== (selectedJob.description || "")) updates.description = detailForm.description;
    if (detailForm.scheduledDate !== (selectedJob.scheduledDate ? (typeof selectedJob.scheduledDate === 'string' && /^\d{4}-\d{2}-\d{2}/.test(selectedJob.scheduledDate) ? selectedJob.scheduledDate.slice(0, 10) : new Date(selectedJob.scheduledDate).toISOString().split('T')[0]) : "")) {
      updates.scheduledDate = detailForm.scheduledDate || null;
    }
    const newAptId = detailForm.apartmentId ? Number(detailForm.apartmentId) : null;
    if (newAptId !== selectedJob.apartmentId) updates.apartmentId = newAptId;
    const newOwnerId = detailForm.ownerUserId ? Number(detailForm.ownerUserId) : null;
    if (newOwnerId !== (selectedJob.ownerUserId || null)) updates.ownerUserId = newOwnerId;
    if (detailForm.isBillable !== (selectedJob.isBillable || false)) updates.isBillable = detailForm.isBillable;
    if (detailForm.costAmount !== (selectedJob.costAmount || "")) updates.costAmount = detailForm.costAmount;
    if (detailForm.costDescription !== (selectedJob.costDescription || "")) updates.costDescription = detailForm.costDescription;
    if (Object.keys(updates).length > 0) {
      updateMutation.mutate({ id: selectedJob.id, data: updates });
    }
    setEditingDetail(false);
  }, [selectedJob, detailForm, updateMutation]);

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => { await apiRequest("DELETE", `/api/admin/maintenance-jobs/${id}`); },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/maintenance-jobs"] });
      setSelectedJob(null);
      toast({ title: "Job deleted" });
    },
  });

  const { data: photos = [] } = useQuery<any[]>({
    queryKey: ["/api/admin/maintenance-jobs", selectedJob?.id, "photos"],
    queryFn: async () => {
      if (!selectedJob?.id) return [];
      const res = await fetch(`/api/admin/maintenance-jobs/${selectedJob.id}/photos`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!selectedJob?.id,
  });

  const uploadPhotoMutation = useMutation({
    mutationFn: async ({ jobId, file, phase, caption }: { jobId: number; file: File; phase: string; caption: string }) => {
      const formData = new FormData();
      formData.append("photo", file);
      formData.append("phase", phase);
      if (caption) formData.append("caption", caption);
      const res = await fetch(`/api/admin/maintenance-jobs/${jobId}/photos`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      if (!res.ok) throw new Error("Upload failed");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/maintenance-jobs", selectedJob?.id, "photos"] });
      setPhotoCaption("");
      toast({ title: "Photo uploaded" });
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const deletePhotoMutation = useMutation({
    mutationFn: async ({ jobId, photoId }: { jobId: number; photoId: number }) => {
      await apiRequest("DELETE", `/api/admin/maintenance-jobs/${jobId}/photos/${photoId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/maintenance-jobs", selectedJob?.id, "photos"] });
      toast({ title: "Photo deleted" });
    },
  });

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedJob) return;
    uploadPhotoMutation.mutate({ jobId: selectedJob.id, file, phase: photoPhase, caption: photoCaption });
    if (photoInputRef.current) photoInputRef.current.value = "";
  };

  const [estimateOpen, setEstimateOpen] = useState(false);
  const [estForm, setEstForm] = useState({ docType: "estimate" as string, notes: "", lineItems: [{ description: "", quantity: 1, unitCost: "", total: "" }] });
  const [recipientInfo, setRecipientInfo] = useState({
    contactName: "", companyName: "", email: "", phone: "",
    addressLine1: "", addressLine2: "", city: "", state: "", postalCode: "", country: "",
  });

  const { data: allEstimates = [] } = useQuery<any[]>({
    queryKey: ["/api/admin/maintenance-estimates"],
  });

  const { data: billingProfile } = useQuery<any>({
    queryKey: ["/api/admin/maintenance-jobs", selectedJob?.id, "billing-profile"],
    queryFn: async () => {
      if (!selectedJob?.id) return null;
      const r = await fetch(`/api/admin/maintenance-jobs/${selectedJob.id}/billing-profile`, { credentials: "include" });
      return r.ok ? r.json() : null;
    },
    enabled: !!selectedJob?.id,
  });

  const jobEstimates = (allEstimates as any[]).filter((e: any) => e.maintenanceJobId === selectedJob?.id);

  const openEstimateDialog = (job: any) => {
    const existing = jobEstimates.find((e: any) => e.maintenanceJobId === job.id);
    if (existing) {
      setEstForm({
        docType: existing.docType || "estimate",
        notes: existing.notes || "",
        lineItems: (existing.lineItems as any[]).length > 0 ? existing.lineItems : [{ description: job.costDescription || job.title, quantity: 1, unitCost: String(job.costAmount || "0.00"), total: String(job.costAmount || "0.00") }],
      });
      // Pre-fill recipient from existing snapshot if available
      const snap = existing.recipientSnapshot as any;
      if (snap) {
        setRecipientInfo({
          contactName: snap.contactName || "", companyName: snap.companyName || "",
          email: snap.email || "", phone: snap.phone || "",
          addressLine1: snap.addressLine1 || "", addressLine2: snap.addressLine2 || "",
          city: snap.city || "", state: snap.state || "", postalCode: snap.postalCode || "", country: snap.country || "",
        });
      } else {
        setRecipientInfo({
          contactName: billingProfile?.ownerName || "", companyName: billingProfile?.companyName || "",
          email: billingProfile?.billingEmail || billingProfile?.ownerEmail || "",
          phone: billingProfile?.billingPhone || "",
          addressLine1: billingProfile?.addressLine1 || "", addressLine2: billingProfile?.addressLine2 || "",
          city: billingProfile?.city || "", state: billingProfile?.state || "",
          postalCode: billingProfile?.postalCode || "", country: billingProfile?.country || "",
        });
      }
    } else {
      setEstForm({
        docType: "estimate",
        notes: "",
        lineItems: [{ description: job.costDescription || job.title, quantity: 1, unitCost: String(job.costAmount || "0.00"), total: String(job.costAmount || "0.00") }],
      });
      setRecipientInfo({
        contactName: billingProfile?.ownerName || "", companyName: billingProfile?.companyName || "",
        email: billingProfile?.billingEmail || billingProfile?.ownerEmail || "",
        phone: billingProfile?.billingPhone || "",
        addressLine1: billingProfile?.addressLine1 || "", addressLine2: billingProfile?.addressLine2 || "",
        city: billingProfile?.city || "", state: billingProfile?.state || "",
        postalCode: billingProfile?.postalCode || "", country: billingProfile?.country || "",
      });
    }
    setEstimateOpen(true);
  };

  const updateEstLineItem = (idx: number, field: string, value: string | number) => {
    setEstForm(prev => {
      const items = [...prev.lineItems];
      items[idx] = { ...items[idx], [field]: value };
      if (field === "quantity" || field === "unitCost") {
        items[idx].total = (Number(items[idx].quantity || 0) * Number(items[idx].unitCost || 0)).toFixed(2);
      }
      return { ...prev, lineItems: items };
    });
  };

  const addEstLineItem = () => setEstForm(prev => ({ ...prev, lineItems: [...prev.lineItems, { description: "", quantity: 1, unitCost: "", total: "" }] }));
  const removeEstLineItem = (idx: number) => setEstForm(prev => ({ ...prev, lineItems: prev.lineItems.filter((_, i) => i !== idx) }));

  const estSubtotal = estForm.lineItems.reduce((s, it) => s + Number(it.total || 0), 0).toFixed(2);

  const createEstimateMutation = useMutation({
    mutationFn: async (andSend: boolean) => {
      const existing = jobEstimates.find((e: any) => e.maintenanceJobId === selectedJob?.id);
      let estimate: any;
      if (existing) {
        const r = await apiRequest("PATCH", `/api/admin/maintenance-estimates/${existing.id}`, { lineItems: estForm.lineItems, notes: estForm.notes, docType: estForm.docType });
        estimate = await r.json();
      } else {
        const r = await apiRequest("POST", "/api/admin/maintenance-estimates", { maintenanceJobId: selectedJob?.id, lineItems: estForm.lineItems, notes: estForm.notes, docType: estForm.docType });
        estimate = await r.json();
      }
      if (andSend) {
        const email = recipientInfo.email;
        if (!email) throw new Error("Recipient email is required. Please fill in the Send To section.");
        const r = await apiRequest("POST", `/api/admin/maintenance-estimates/${estimate.id}/send`, {
          recipientSnapshot: {
            contactName: recipientInfo.contactName || undefined,
            companyName: recipientInfo.companyName || undefined,
            email: recipientInfo.email,
            phone: recipientInfo.phone || undefined,
            addressLine1: recipientInfo.addressLine1 || undefined,
            addressLine2: recipientInfo.addressLine2 || undefined,
            city: recipientInfo.city || undefined,
            state: recipientInfo.state || undefined,
            postalCode: recipientInfo.postalCode || undefined,
            country: recipientInfo.country || undefined,
          },
        });
        return r.json();
      }
      return estimate;
    },
    onSuccess: (_, andSend) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/maintenance-estimates"] });
      setEstimateOpen(false);
      toast({ title: andSend ? "Estimate sent via email" : "Estimate saved as draft" });
    },
    onError: (err: Error) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const downloadEstimatePdf = async (estimateId: number) => {
    const r = await fetch(`/api/admin/maintenance-estimates/${estimateId}/pdf`, { credentials: "include" });
    if (!r.ok) { toast({ title: "Error", description: "Failed to download PDF", variant: "destructive" }); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `estimate-${estimateId}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  const beforePhotos = (photos as any[]).filter(p => p.phase === "before");
  const afterPhotos = (photos as any[]).filter(p => p.phase === "after");

  const allMaintPhotos = [
    ...beforePhotos.map((p: any) => ({ id: p.id, url: p.url, label: `Before${p.caption ? ' - ' + p.caption : ''}` })),
    ...afterPhotos.map((p: any) => ({ id: p.id, url: p.url, label: `After${p.caption ? ' - ' + p.caption : ''}` })),
  ];
  const getMaintPhotoIndex = (photo: any) => allMaintPhotos.findIndex(p => p.id === photo.id);

  const today = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; }, []);
  const calendarDays = useMemo(() => {
    const days: Date[] = [];
    for (let i = 0; i < 7; i++) { const d = new Date(today); d.setDate(d.getDate() + i + calendarOffset); days.push(d); }
    return days;
  }, [today, calendarOffset]);

  const calendarApartments = useMemo(() => {
    const aptIds = new Set((jobs as any[]).filter(j => j.apartmentId).map(j => j.apartmentId));
    const result = typedApts.filter(a => aptIds.has(a.id)).sort((a, b) => a.name.localeCompare(b.name));
    const hasNoApt = (jobs as any[]).some(j => !j.apartmentId);
    if (hasNoApt) result.push({ id: 0, name: "General" } as any);
    return result;
  }, [jobs, typedApts]);

  const getJobsForDay = (aptId: number, day: Date) => {
    const dayKey = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(day.getDate()).padStart(2, '0')}`;
    return filtered.filter(j => {
      const jAptId = j.apartmentId || 0;
      if (jAptId !== aptId) return false;
      const raw = j.scheduledDate || j.createdAt;
      if (!raw) return false;
      const jobKey = typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}/.test(raw)
        ? raw.slice(0, 10)
        : new Date(raw).toISOString().split('T')[0];
      return jobKey === dayKey;
    });
  };

  const getJobColor = (job: any) => {
    if (job.priority === "urgent" || job.priority === "high") return "bg-red-500 text-white";
    return `${(statusConfig[job.status] || statusConfig.pending).color} text-white`;
  };

  const pendingCount = (jobs as any[]).filter(j => j.status === "pending" || j.status === "assigned" || j.status === "pending_approval" || j.status === "approved").length;
  const inProgressCount = (jobs as any[]).filter(j => j.status === "in_progress").length;
  const completedCount = (jobs as any[]).filter(j => j.status === "completed").length;

  const handleCreate = () => {
    if (!form.title.trim()) return;
    if (!form.apartmentId || form.apartmentId === "none") {
      toast({ title: "Apartment required", description: "Please select an apartment for this maintenance job.", variant: "destructive" });
      return;
    }
    createMutation.mutate({
      title: form.title,
      description: form.description || null,
      priority: form.priority,
      apartmentId: Number(form.apartmentId),
      ownerUserId: form.ownerUserId && form.ownerUserId !== "none" ? Number(form.ownerUserId) : null,
      assignedToUserId: form.assignedToUserId && form.assignedToUserId !== "none" ? Number(form.assignedToUserId) : null,
      scheduledDate: form.scheduledDate || null,
      isBillable: form.isBillable,
      costAmount: form.costAmount || "0.00",
      costDescription: form.costDescription || null,
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="font-display text-2xl font-bold" data-testid="text-maintenance-title">Maintenance Jobs</h1>
            <p className="text-muted-foreground">Schedule, assign, and track maintenance work across your properties.</p>
          </div>
          <div className="flex gap-1">
            <Button
              variant="outline"
              onClick={() => autoScheduleMutation.mutate()}
              disabled={autoScheduleMutation.isPending}
              data-testid="button-auto-schedule"
            >
              <Calendar className="w-4 h-4 mr-1" />
              {autoScheduleMutation.isPending ? "Scheduling..." : "Auto-schedule"}
            </Button>
            <Button onClick={() => setCreateOpen(true)} data-testid="button-create-maintenance">
              <Plus className="w-4 h-4 mr-1" />
              New Job
            </Button>
            <Button variant={viewMode === "calendar" ? "default" : "outline"} size="sm" onClick={() => setViewMode("calendar")} data-testid="button-maint-view-calendar">
              <CalendarDays className="w-4 h-4 mr-1" />Calendar
            </Button>
            <Button variant={viewMode === "list" ? "default" : "outline"} size="sm" onClick={() => setViewMode("list")} data-testid="button-maint-view-list">
              <List className="w-4 h-4 mr-1" />List
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-destructive">{pendingCount}</p><p className="text-xs text-muted-foreground">Pending</p></CardContent></Card>
          <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-primary">{inProgressCount}</p><p className="text-xs text-muted-foreground">In Progress</p></CardContent></Card>
          <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-green-600">{completedCount}</p><p className="text-xs text-muted-foreground">Completed</p></CardContent></Card>
        </div>

        <div className="flex gap-1 flex-wrap">
          {(["all", "pending", "pending_approval", "approved", "assigned", "in_progress", "completed", "cancelled"] as FilterStatus[]).map(s => (
            <Button key={s} variant={filter === s ? "default" : "outline"} size="sm" onClick={() => setFilter(s)} data-testid={`button-maint-filter-${s}`}>
              {s === "all" ? "All" : s === "in_progress" ? "Active" : s === "pending_approval" ? "Awaiting" : s.charAt(0).toUpperCase() + s.slice(1)}
            </Button>
          ))}
        </div>

        {viewMode === "calendar" && (
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Weekly Schedule</CardTitle>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="icon" onClick={() => setCalendarOffset(o => o - 7)} data-testid="button-maint-cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" onClick={() => setCalendarOffset(0)} data-testid="button-maint-cal-today">Today</Button>
                  <Button variant="outline" size="icon" onClick={() => setCalendarOffset(o => o + 7)} data-testid="button-maint-cal-next"><ChevronRight className="w-4 h-4" /></Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <div className="min-w-[700px]">
                  <div className="grid border-b" style={{ gridTemplateColumns: '160px repeat(7, 1fr)' }}>
                    <div className="p-2 text-xs font-medium text-muted-foreground border-r">Location</div>
                    {calendarDays.map((day, i) => (
                      <div key={i} className={`p-2 text-center text-xs font-medium border-r last:border-r-0 ${isSameDay(day, today) ? "bg-primary/5 text-primary" : "text-muted-foreground"}`}>
                        <div>{day.toLocaleDateString("en-US", { weekday: "short" })}</div>
                        <div className="font-bold text-sm">{day.getDate()}</div>
                      </div>
                    ))}
                  </div>
                  {calendarApartments.map(apt => (
                    <div key={apt.id} className="grid border-b last:border-b-0" style={{ gridTemplateColumns: '160px repeat(7, 1fr)' }}>
                      <div className="p-2 text-sm font-medium truncate border-r flex items-center gap-1">
                        {apt.id === 0 ? <Wrench className="w-3 h-3 text-muted-foreground shrink-0" /> : <Home className="w-3 h-3 text-muted-foreground shrink-0" />}
                        <span className="truncate">{apt.name}</span>
                      </div>
                      {calendarDays.map((day, di) => {
                        const dayJobs = getJobsForDay(apt.id, day);
                        const cellKey = `${apt.id}-${di}`;
                        return (
                          <div
                            key={di}
                            className={`p-1 border-r last:border-r-0 min-h-[48px] transition-colors ${isSameDay(day, today) ? "bg-primary/5" : ""} ${dragOverCell === cellKey ? "bg-primary/15 ring-1 ring-primary/40 ring-inset" : ""}`}
                            onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverCell(cellKey); }}
                            onDragLeave={() => setDragOverCell(null)}
                            onDrop={e => handleMaintDrop(e, day)}
                          >
                            {dayJobs.map(job => (
                              <div key={job.id}
                                draggable
                                onDragStart={e => handleMaintDragStart(e, job.id)}
                                className={`rounded px-1.5 py-0.5 mb-0.5 text-[10px] font-medium truncate cursor-grab active:cursor-grabbing ${getJobColor(job)}`}
                                title={`${job.title} - ${getAssigneeName(job.assignedToUserId)}\nDrag to reschedule`}
                                onClick={() => { const j = selectedJob?.id === job.id ? null : job; setSelectedJob(j); setEditingDetail(false); if (j) setMaintSheetOpen(true); }}
                                data-testid={`maint-cal-job-${job.id}`}
                              >
                                {(job.priority === "urgent" || job.priority === "high") && "! "}
                                {job.title.length > 12 ? job.title.slice(0, 12) + "…" : job.title}
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-4 p-3 text-xs text-muted-foreground border-t">
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-red-500/80" /><span>Pending</span></div>
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-amber-500/80" /><span>Assigned</span></div>
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-blue-500/80" /><span>In Progress</span></div>
                <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-green-500/80" /><span>Completed</span></div>
              </div>
            </CardContent>
          </Card>
        )}

        {selectedJob && (
          <Card className={`border-l-4 ${selectedJob.priority === "urgent" || selectedJob.priority === "high" ? "border-l-destructive" : "border-l-primary"}`} data-testid="card-selected-maint-job">
            <CardContent className="p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-semibold text-sm">{selectedJob.title}</span>
                    <Badge variant={(priorityConfig[selectedJob.priority || "medium"] || priorityConfig.medium).variant} className="text-[10px] py-0 px-1.5">
                      {(priorityConfig[selectedJob.priority || "medium"] || priorityConfig.medium).label}
                    </Badge>
                    <Badge variant={(statusConfig[selectedJob.status] || statusConfig.pending).variant} className="text-[10px] py-0 px-1.5">
                      {(statusConfig[selectedJob.status] || statusConfig.pending).label}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
                    {selectedJob.apartmentId && <span className="flex items-center gap-1"><Home className="w-3 h-3" />{getApartmentName(selectedJob.apartmentId)}</span>}
                    <span className="flex items-center gap-1"><User className="w-3 h-3" />{getAssigneeName(selectedJob.assignedToUserId)}</span>
                    {selectedJob.scheduledDate && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(selectedJob.scheduledDate + (typeof selectedJob.scheduledDate === 'string' && !selectedJob.scheduledDate.includes('T') ? 'T12:00:00' : '')).toLocaleDateString()}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button size="sm" onClick={() => setMaintSheetOpen(true)} data-testid="button-open-maint-profile">
                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" />Open
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setSelectedJob(null); setEditingDetail(false); }} data-testid="button-close-maint-detail">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {viewMode === "list" && (
          <>
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : filtered.length === 0 ? (
              <Card><CardContent className="p-8 text-center text-muted-foreground"><Wrench className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>No maintenance jobs found.</p></CardContent></Card>
            ) : (
              <div className="space-y-3">
                {filtered.map((job: any) => {
                  const config = statusConfig[job.status] || statusConfig.pending;
                  const pConfig = priorityConfig[job.priority || "medium"] || priorityConfig.medium;
                  const isUrgent = job.priority === "urgent" || job.priority === "high";
                  const jobEst = (allEstimates as any[]).find((e: any) => e.maintenanceJobId === job.id);
                  return (
                    <Card key={job.id} className={`cursor-pointer ${isUrgent ? "border-destructive" : ""}`} onClick={() => { const j = selectedJob?.id === job.id ? null : job; setSelectedJob(j); setEditingDetail(false); if (j) setMaintSheetOpen(true); }} data-testid={`card-maint-job-${job.id}`}>
                      <CardContent className="p-4 space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="font-medium">{job.title}</p>
                            {job.apartmentId && <p className="text-sm text-muted-foreground flex items-center gap-1"><Home className="w-3 h-3" />{getApartmentName(job.apartmentId)}</p>}
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            {jobEst && (
                              <Badge variant={jobEst.status === "approved" ? "default" : jobEst.status === "rejected" ? "destructive" : jobEst.status === "sent" ? "default" : "secondary"} data-testid={`badge-est-list-${job.id}`}>
                                <FileText className="w-3 h-3 mr-0.5" />
                                {jobEst.status === "sent" ? "Est. Sent" : jobEst.status === "approved" ? "Est. Approved" : jobEst.status === "rejected" ? "Est. Rejected" : "Est. Draft"}
                              </Badge>
                            )}
                            <Badge variant={pConfig.variant}>{pConfig.label}</Badge>
                            <Badge variant={config.variant}>{config.label}</Badge>
                          </div>
                        </div>
                        {job.description && <p className="text-sm text-muted-foreground line-clamp-2">{job.description}</p>}
                        <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                          <span className="flex items-center gap-1"><User className="w-3 h-3" />{getAssigneeName(job.assignedToUserId)}</span>
                          {job.scheduledDate && <span><Calendar className="w-3 h-3 inline mr-1" />{new Date(job.scheduledDate + (typeof job.scheduledDate === 'string' && !job.scheduledDate.includes('T') ? 'T12:00:00' : '')).toLocaleDateString()}</span>}
                          {job.isBillable && <span><DollarSign className="w-3 h-3 inline mr-1" />${Number(job.costAmount || 0).toFixed(2)}</span>}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}

        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>New Maintenance Job</DialogTitle>
              <DialogDescription>Create a maintenance task for your team.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div><Label>Title *</Label><Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Fix leaking faucet" data-testid="input-maint-title" /></div>
              <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Details..." className="resize-none" rows={3} data-testid="input-maint-description" /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Priority</Label>
                  <Select value={form.priority} onValueChange={v => setForm(p => ({ ...p, priority: v }))}>
                    <SelectTrigger data-testid="select-maint-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="urgent">Urgent</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Apartment <span className="text-destructive">*</span></Label>
                  <SearchableSelect
                    value={form.apartmentId}
                    onValueChange={v => setForm(p => ({ ...p, apartmentId: v }))}
                    placeholder="Select apartment"
                    searchPlaceholder="Search apartment..."
                    options={typedApts.map(a => ({ value: String(a.id), label: a.name }))}
                    className="w-full"
                    data-testid="select-maint-apartment"
                  />
                </div>
              </div>
              <div>
                <Label>Property Manager</Label>
                <SearchableSelect
                  value={form.ownerUserId}
                  onValueChange={v => setForm(p => ({ ...p, ownerUserId: v }))}
                  placeholder="None"
                  searchPlaceholder="Search PM..."
                  options={[
                    { value: "none", label: "None" },
                    ...ownerUsers.filter((u: any) => u.isActive !== false).map((u: any) => ({ value: String(u.id), label: u.fullName || u.username })),
                  ]}
                  className="w-full"
                  data-testid="select-maint-owner"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Assign To</Label>
                  <SearchableSelect
                    value={form.assignedToUserId}
                    onValueChange={v => setForm(p => ({ ...p, assignedToUserId: v }))}
                    placeholder="Optional"
                    searchPlaceholder="Search user..."
                    options={[
                      { value: "none", label: "Unassigned" },
                      ...maintenanceUsers.filter((u: any) => u.isActive !== false).map((u: any) => ({ value: String(u.id), label: u.fullName || u.username })),
                    ]}
                    className="w-full"
                    data-testid="select-maint-assignee"
                  />
                </div>
                <div>
                  <Label>Scheduled Date</Label>
                  <Input type="date" value={form.scheduledDate} onChange={e => setForm(p => ({ ...p, scheduledDate: e.target.value }))} data-testid="input-maint-scheduled" />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={form.isBillable} onCheckedChange={v => setForm(p => ({ ...p, isBillable: v }))} data-testid="switch-maint-billable" />
                <Label>Billable</Label>
              </div>
              {form.isBillable && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label>Cost</Label><Input type="number" step="0.01" value={form.costAmount} onChange={e => setForm(p => ({ ...p, costAmount: e.target.value }))} placeholder="0.00" data-testid="input-maint-cost" /></div>
                  <div><Label>Cost Note</Label><Input value={form.costDescription} onChange={e => setForm(p => ({ ...p, costDescription: e.target.value }))} placeholder="Parts, labor..." data-testid="input-maint-cost-desc" /></div>
                </div>
              )}
              <Button className="w-full" onClick={handleCreate} disabled={!form.title.trim() || createMutation.isPending} data-testid="button-submit-maintenance">
                {createMutation.isPending ? "Creating..." : "Create Job"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={estimateOpen} onOpenChange={setEstimateOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5" />{jobEstimates.length > 0 ? "Edit" : "Generate"} Repair Estimate</DialogTitle>
              <DialogDescription>{selectedJob?.title} — {getApartmentName(selectedJob?.apartmentId)}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Document Type</Label>
                <Select value={estForm.docType} onValueChange={v => setEstForm(p => ({ ...p, docType: v }))}>
                  <SelectTrigger data-testid="select-est-doc-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="estimate">Estimate</SelectItem>
                    <SelectItem value="invoice">Invoice</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Line Items</Label>
                  <Button variant="ghost" size="sm" onClick={addEstLineItem} data-testid="button-add-est-line">
                    <Plus className="w-3 h-3 mr-1" />Add Item
                  </Button>
                </div>
                <div className="space-y-2">
                  {estForm.lineItems.map((item, idx) => (
                    <div key={idx} className="grid grid-cols-[1fr_60px_90px_90px_28px] gap-1 items-end">
                      {idx === 0 && <>
                        <Label className="text-[10px] text-muted-foreground">Description</Label>
                        <Label className="text-[10px] text-muted-foreground">Qty</Label>
                        <Label className="text-[10px] text-muted-foreground">Unit Cost</Label>
                        <Label className="text-[10px] text-muted-foreground">Total</Label>
                        <span />
                      </>}
                      <Input value={item.description} onChange={e => updateEstLineItem(idx, "description", e.target.value)} placeholder="Description" className="text-sm" data-testid={`input-est-desc-${idx}`} />
                      <Input type="number" min={1} value={item.quantity} onChange={e => updateEstLineItem(idx, "quantity", Number(e.target.value))} className="text-sm" data-testid={`input-est-qty-${idx}`} />
                      <Input type="number" step="0.01" value={item.unitCost} onChange={e => updateEstLineItem(idx, "unitCost", e.target.value)} placeholder="0.00" className="text-sm" data-testid={`input-est-unit-${idx}`} />
                      <Input value={`$${Number(item.total || 0).toFixed(2)}`} readOnly className="text-sm bg-muted" />
                      {estForm.lineItems.length > 1 && (
                        <Button variant="ghost" size="icon" className="h-8 w-7" onClick={() => removeEstLineItem(idx)} data-testid={`button-remove-est-line-${idx}`}>
                          <X className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex justify-end mt-2">
                  <p className="font-semibold text-sm">Total: ${estSubtotal}</p>
                </div>
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea value={estForm.notes} onChange={e => setEstForm(p => ({ ...p, notes: e.target.value }))} rows={3} className="resize-none" placeholder="Additional notes or terms..." data-testid="input-est-notes" />
              </div>

              <div className="border rounded-lg p-3 bg-muted/20 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium flex items-center gap-1.5">
                    <Send className="w-3.5 h-3.5" /> Send To
                  </h4>
                  {billingProfile && (
                    <Button variant="ghost" size="sm" className="text-xs h-6 px-2" onClick={() => setRecipientInfo({
                      contactName: billingProfile.ownerName || "", companyName: billingProfile.companyName || "",
                      email: billingProfile.billingEmail || billingProfile.ownerEmail || "",
                      phone: billingProfile.billingPhone || "",
                      addressLine1: billingProfile.addressLine1 || "", addressLine2: billingProfile.addressLine2 || "",
                      city: billingProfile.city || "", state: billingProfile.state || "",
                      postalCode: billingProfile.postalCode || "", country: billingProfile.country || "",
                    })} data-testid="button-reset-recipient">
                      Reset from billing profile
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Contact Name</Label>
                    <Input value={recipientInfo.contactName} onChange={e => setRecipientInfo(p => ({ ...p, contactName: e.target.value }))} placeholder="John Doe" className="h-8 text-sm" data-testid="input-recipient-name" />
                  </div>
                  <div>
                    <Label className="text-xs">Company</Label>
                    <Input value={recipientInfo.companyName} onChange={e => setRecipientInfo(p => ({ ...p, companyName: e.target.value }))} placeholder="Acme LLC" className="h-8 text-sm" data-testid="input-recipient-company" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Email <span className="text-destructive">*</span></Label>
                    <Input type="email" value={recipientInfo.email} onChange={e => setRecipientInfo(p => ({ ...p, email: e.target.value }))} placeholder="owner@example.com" className="h-8 text-sm" data-testid="input-recipient-email" />
                  </div>
                  <div>
                    <Label className="text-xs">Phone</Label>
                    <Input value={recipientInfo.phone} onChange={e => setRecipientInfo(p => ({ ...p, phone: e.target.value }))} placeholder="+1 555 0100" className="h-8 text-sm" data-testid="input-recipient-phone" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Address Line 1</Label>
                  <Input value={recipientInfo.addressLine1} onChange={e => setRecipientInfo(p => ({ ...p, addressLine1: e.target.value }))} placeholder="123 Main St" className="h-8 text-sm" data-testid="input-recipient-addr1" />
                </div>
                <div>
                  <Label className="text-xs">Address Line 2</Label>
                  <Input value={recipientInfo.addressLine2} onChange={e => setRecipientInfo(p => ({ ...p, addressLine2: e.target.value }))} placeholder="Suite 100" className="h-8 text-sm" data-testid="input-recipient-addr2" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs">City</Label>
                    <Input value={recipientInfo.city} onChange={e => setRecipientInfo(p => ({ ...p, city: e.target.value }))} placeholder="Miami" className="h-8 text-sm" data-testid="input-recipient-city" />
                  </div>
                  <div>
                    <Label className="text-xs">State</Label>
                    <Input value={recipientInfo.state} onChange={e => setRecipientInfo(p => ({ ...p, state: e.target.value }))} placeholder="FL" className="h-8 text-sm" data-testid="input-recipient-state" />
                  </div>
                  <div>
                    <Label className="text-xs">Postal Code</Label>
                    <Input value={recipientInfo.postalCode} onChange={e => setRecipientInfo(p => ({ ...p, postalCode: e.target.value }))} placeholder="33101" className="h-8 text-sm" data-testid="input-recipient-zip" />
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => createEstimateMutation.mutate(false)} disabled={createEstimateMutation.isPending || estForm.lineItems.every(it => !it.description)} data-testid="button-save-est-draft">
                  {createEstimateMutation.isPending ? "Saving..." : "Save Draft"}
                </Button>
                <Button className="flex-1" onClick={() => createEstimateMutation.mutate(true)} disabled={createEstimateMutation.isPending || estForm.lineItems.every(it => !it.description) || !recipientInfo.email} data-testid="button-send-est-email">
                  <Send className="w-4 h-4 mr-1" />
                  {createEstimateMutation.isPending ? "Sending..." : "Save & Send Email"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Maintenance Job Profile Sheet */}
        <Sheet open={maintSheetOpen} onOpenChange={setMaintSheetOpen}>
          <SheetContent side="right" className="sm:max-w-2xl w-full p-0 flex flex-col overflow-hidden">
            {activeMaintJob && (
              <>
                {/* Header */}
                <div className="px-6 pt-5 pb-4 border-b bg-muted/30 shrink-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      {editingDetail ? (
                        <Input
                          value={detailForm.title}
                          onChange={e => setDetailForm(p => ({ ...p, title: e.target.value }))}
                          className="text-lg font-semibold h-8 border-0 px-0 bg-transparent focus-visible:ring-0"
                          data-testid="input-sheet-maint-title"
                        />
                      ) : (
                        <h2 className="text-lg font-semibold leading-tight truncate">{activeMaintJob.title}</h2>
                      )}
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        {activeMaintJob.apartmentId && (
                          <span className="text-sm text-muted-foreground flex items-center gap-1">
                            <Home className="w-3.5 h-3.5" />{getApartmentName(activeMaintJob.apartmentId)}
                          </span>
                        )}
                        <Badge variant={(priorityConfig[activeMaintJob.priority || "medium"] || priorityConfig.medium).variant} className="text-[10px] py-0 px-1.5">
                          {(priorityConfig[activeMaintJob.priority || "medium"] || priorityConfig.medium).label}
                        </Badge>
                        <Badge variant={(statusConfig[activeMaintJob.status] || statusConfig.pending).variant} className="text-[10px] py-0 px-1.5">
                          {(statusConfig[activeMaintJob.status] || statusConfig.pending).label}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {editingDetail ? (
                        <>
                          <Button size="sm" onClick={saveEditDetail} data-testid="button-sheet-save-edit">Save</Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingDetail(false)} data-testid="button-sheet-cancel-edit">Cancel</Button>
                        </>
                      ) : (
                        <Button size="sm" variant="outline" onClick={startEditDetail} data-testid="button-sheet-edit">
                          <Pencil className="w-3.5 h-3.5 mr-1" />Edit
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Scrollable body */}
                <div className="flex-1 overflow-y-auto">
                  <div className="px-6 py-4 space-y-5">

                    {/* Edit form fields (when editing) */}
                    {editingDetail && (
                      <div className="space-y-3 border rounded-lg p-3 bg-muted/20">
                        <div><Label className="text-xs">Description</Label><Textarea value={detailForm.description} onChange={e => setDetailForm(p => ({ ...p, description: e.target.value }))} rows={2} className="resize-none mt-1" data-testid="input-sheet-maint-description" /></div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Apartment</Label>
                            <SearchableSelect
                              value={detailForm.apartmentId || "none"}
                              onValueChange={v => setDetailForm(p => ({ ...p, apartmentId: v === "none" ? "" : v }))}
                              placeholder="None"
                              searchPlaceholder="Search apartment..."
                              options={[
                                { value: "none", label: "None" },
                                ...typedApts.map((a: Apartment) => ({ value: String(a.id), label: a.name })),
                              ]}
                              className="w-full mt-1"
                              data-testid="select-sheet-maint-apt"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Property Manager</Label>
                            <SearchableSelect
                              value={detailForm.ownerUserId || "none"}
                              onValueChange={v => setDetailForm(p => ({ ...p, ownerUserId: v === "none" ? "" : v }))}
                              placeholder="None"
                              searchPlaceholder="Search PM..."
                              options={[
                                { value: "none", label: "None" },
                                ...ownerUsers.filter((u: any) => u.isActive !== false).map((u: any) => ({ value: String(u.id), label: u.fullName || u.username })),
                              ]}
                              className="w-full mt-1"
                              data-testid="select-sheet-maint-owner"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div><Label className="text-xs">Scheduled Date</Label><Input type="date" className="mt-1" value={detailForm.scheduledDate} onChange={e => setDetailForm(p => ({ ...p, scheduledDate: e.target.value }))} data-testid="input-sheet-maint-date" /></div>
                          <div className="flex items-end gap-2 pb-0.5">
                            <div className="flex items-center gap-2">
                              <Switch checked={detailForm.isBillable} onCheckedChange={v => setDetailForm(p => ({ ...p, isBillable: v }))} data-testid="switch-sheet-maint-billable" />
                              <Label className="text-xs">Billable</Label>
                            </div>
                          </div>
                        </div>
                        {detailForm.isBillable && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div><Label className="text-xs">Cost</Label><Input type="number" step="0.01" className="mt-1" value={detailForm.costAmount} onChange={e => setDetailForm(p => ({ ...p, costAmount: e.target.value }))} placeholder="0.00" data-testid="input-sheet-maint-cost" /></div>
                            <div><Label className="text-xs">Cost Note</Label><Input className="mt-1" value={detailForm.costDescription} onChange={e => setDetailForm(p => ({ ...p, costDescription: e.target.value }))} placeholder="Parts, labor..." data-testid="input-sheet-maint-cost-desc" /></div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Description (read mode) */}
                    {!editingDetail && activeMaintJob.description && (
                      <p className="text-sm text-muted-foreground">{activeMaintJob.description}</p>
                    )}

                    {/* Info grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Assigned To</p>
                        <SearchableSelect
                          value={String(activeMaintJob.assignedToUserId || "none")}
                          onValueChange={v => updateMutation.mutate({ id: activeMaintJob.id, data: { assignedToUserId: v === "none" ? null : Number(v) } })}
                          placeholder="Unassigned"
                          searchPlaceholder="Search user..."
                          options={[
                            { value: "none", label: "Unassigned" },
                            ...maintenanceUsers.filter((u: any) => u.isActive !== false).map((u: any) => ({ value: String(u.id), label: u.fullName || u.username })),
                          ]}
                          className="h-8 w-full"
                          data-testid="select-sheet-maint-assignee"
                        />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Status</p>
                        <Select value={activeMaintJob.status} onValueChange={v => updateMutation.mutate({ id: activeMaintJob.id, data: { status: v } })}>
                          <SelectTrigger className="h-8" data-testid="select-sheet-maint-status"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="pending_approval">Awaiting Approval</SelectItem>
                            <SelectItem value="approved">Approved</SelectItem>
                            <SelectItem value="assigned">Assigned</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Priority</p>
                        <Select value={activeMaintJob.priority || "medium"} onValueChange={v => updateMutation.mutate({ id: activeMaintJob.id, data: { priority: v } })}>
                          <SelectTrigger className="h-8" data-testid="select-sheet-maint-priority"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="urgent">Urgent</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="low">Low</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {activeMaintJob.scheduledDate && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Scheduled</p>
                          <p className="text-sm font-medium">
                            {new Date(activeMaintJob.scheduledDate + (typeof activeMaintJob.scheduledDate === 'string' && !activeMaintJob.scheduledDate.includes('T') ? 'T12:00:00' : '')).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                      {activeMaintJob.isBillable && (
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Billable Amount</p>
                          <p className="text-sm font-medium">${Number(activeMaintJob.costAmount || 0).toFixed(2)}</p>
                        </div>
                      )}
                    </div>

                    {/* Repair Estimate */}
                    <div className="border rounded-lg p-3 bg-muted/30 space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-sm flex items-center gap-1">
                          <FileText className="w-4 h-4" /> Repair Estimate
                        </h4>
                        <div className="flex items-center gap-1">
                          {jobEstimates.length > 0 && (
                            <>
                              <Badge variant={jobEstimates[0].status === "approved" ? "default" : jobEstimates[0].status === "rejected" ? "destructive" : "secondary"} data-testid="badge-sheet-estimate-status">
                                {jobEstimates[0].status === "sent" ? "Sent" : jobEstimates[0].status === "approved" ? "Approved" : jobEstimates[0].status === "rejected" ? "Rejected" : "Draft"}
                              </Badge>
                              <Button variant="ghost" size="sm" onClick={() => downloadEstimatePdf(jobEstimates[0].id)} data-testid="button-sheet-download-estimate">
                                <Download className="w-3 h-3 mr-1" />PDF
                              </Button>
                            </>
                          )}
                          <Button variant="outline" size="sm" onClick={() => openEstimateDialog(activeMaintJob)} data-testid="button-sheet-generate-estimate">
                            <FileText className="w-3 h-3 mr-1" />
                            {jobEstimates.length > 0 ? "Edit Estimate" : "Generate Estimate"}
                          </Button>
                        </div>
                      </div>
                      {jobEstimates.length > 0 && (
                        <p className="text-xs text-muted-foreground">
                          {jobEstimates[0].estimateNumber} • ${Number(jobEstimates[0].subtotal).toFixed(2)}
                          {jobEstimates[0].sentTo && ` • Sent to ${jobEstimates[0].sentTo}`}
                        </p>
                      )}
                    </div>

                    {/* Photos + Activity tabs */}
                    <Tabs defaultValue="photos" className="w-full">
                      <TabsList className="w-full" data-testid="tabs-sheet-maint">
                        <TabsTrigger value="photos" className="flex-1" data-testid="tab-sheet-photos">Photos</TabsTrigger>
                        <TabsTrigger value="activity" className="flex-1" data-testid="tab-sheet-activity">Activity</TabsTrigger>
                      </TabsList>
                      <TabsContent value="photos" className="pt-3">
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Select value={photoPhase} onValueChange={(v: "before" | "after") => setPhotoPhase(v)}>
                              <SelectTrigger className="w-[90px] text-xs h-8" data-testid="select-sheet-photo-phase"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="before">Before</SelectItem>
                                <SelectItem value="after">After</SelectItem>
                              </SelectContent>
                            </Select>
                            <Input placeholder="Caption..." value={photoCaption} onChange={e => setPhotoCaption(e.target.value)} className="w-[130px] text-xs h-8" data-testid="input-sheet-photo-caption" />
                            <input ref={photoInputRef} type="file" accept="image/*,video/*" className="hidden" onChange={handlePhotoUpload} />
                            <Button variant="outline" size="sm" onClick={() => photoInputRef.current?.click()} disabled={uploadPhotoMutation.isPending} data-testid="button-sheet-upload-photo">
                              <Camera className="w-4 h-4 mr-1" />{uploadPhotoMutation.isPending ? "Uploading..." : "Upload"}
                            </Button>
                          </div>
                          {(beforePhotos.length > 0 || afterPhotos.length > 0) ? (
                            <div className="space-y-3">
                              {beforePhotos.length > 0 && (
                                <div>
                                  <p className="text-xs font-medium text-muted-foreground mb-1">Before</p>
                                  <div className="flex gap-2 flex-wrap">
                                    {beforePhotos.map((photo: any) => {
                                      const isVideo = /\.(mp4|mov|webm|avi|mkv)$/i.test(photo.url || '') || (photo.url || '').includes('video');
                                      return (
                                        <div key={photo.id} className="relative group" data-testid={`photo-sheet-before-${photo.id}`}>
                                          {isVideo ? <video src={photo.url} controls className="w-28 h-20 object-cover rounded-md border" /> : (
                                            <img src={photo.url} alt={photo.caption || "Before"} className="w-24 h-24 object-cover rounded-md border cursor-pointer hover:ring-2 ring-primary transition-all" onClick={() => openLightbox(allMaintPhotos, getMaintPhotoIndex(photo))} />
                                          )}
                                          {photo.caption && <p className="text-[10px] text-muted-foreground mt-0.5 truncate w-24">{photo.caption}</p>}
                                          <button className="absolute top-0.5 right-0.5 bg-destructive text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => { e.stopPropagation(); setDeleteConfirmMaintPhoto({ jobId: activeMaintJob.id, photoId: photo.id, phase: "before" }); }} data-testid={`button-sheet-delete-photo-${photo.id}`}>
                                            <X className="w-3 h-3" />
                                          </button>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                              {afterPhotos.length > 0 && (
                                <div>
                                  <p className="text-xs font-medium text-muted-foreground mb-1">After</p>
                                  <div className="flex gap-2 flex-wrap">
                                    {afterPhotos.map((photo: any) => {
                                      const isVideo = /\.(mp4|mov|webm|avi|mkv)$/i.test(photo.url || '') || (photo.url || '').includes('video');
                                      return (
                                        <div key={photo.id} className="relative group" data-testid={`photo-sheet-after-${photo.id}`}>
                                          {isVideo ? <video src={photo.url} controls className="w-28 h-20 object-cover rounded-md border" /> : (
                                            <img src={photo.url} alt={photo.caption || "After"} className="w-24 h-24 object-cover rounded-md border cursor-pointer hover:ring-2 ring-primary transition-all" onClick={() => openLightbox(allMaintPhotos, getMaintPhotoIndex(photo))} />
                                          )}
                                          {photo.caption && <p className="text-[10px] text-muted-foreground mt-0.5 truncate w-24">{photo.caption}</p>}
                                          <button className="absolute top-0.5 right-0.5 bg-destructive text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => { e.stopPropagation(); setDeleteConfirmMaintPhoto({ jobId: activeMaintJob.id, photoId: photo.id, phase: "after" }); }} data-testid={`button-sheet-delete-photo-after-${photo.id}`}>
                                            <X className="w-3 h-3" />
                                          </button>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-center py-6 text-muted-foreground text-sm">
                              <ImageIcon className="w-8 h-8 mx-auto mb-1 opacity-30" />
                              <p>No photos yet</p>
                            </div>
                          )}
                          {lightboxState && (
                            <PhotoLightbox photos={lightboxState.photos} initialIndex={lightboxState.index} onClose={closeLightbox} />
                          )}
                        </div>
                      </TabsContent>
                      <TabsContent value="activity" className="pt-3">
                        <MaintenanceJobActivityTimeline jobId={activeMaintJob.id} />
                      </TabsContent>
                    </Tabs>
                  </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t bg-background shrink-0 flex items-center justify-between">
                  <Button variant="destructive" size="sm" onClick={() => setDeleteConfirmMaintJob(activeMaintJob)} data-testid="button-sheet-delete-maint">
                    <Trash2 className="w-4 h-4 mr-1.5" />Delete Job
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setMaintSheetOpen(false)} data-testid="button-sheet-close-maint">Close</Button>
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>
      </div>

      <DeleteConfirmDialog
        open={deleteConfirmMaintJob !== null}
        onOpenChange={open => { if (!open) setDeleteConfirmMaintJob(null); }}
        title="Delete this maintenance job?"
        description={deleteConfirmMaintJob ? <>
          You're about to permanently delete the maintenance job <strong>"{deleteConfirmMaintJob.title}"</strong>{deleteConfirmMaintJob.apartmentId ? <> for <strong>{getApartmentName(deleteConfirmMaintJob.apartmentId)}</strong></> : null}.
          {deleteConfirmMaintJob.status === "completed" && <span className="block mt-2 text-destructive font-medium">This job is marked as completed. All before/after photos and the activity history will be lost.</span>}
          <span className="block mt-2">This cannot be undone.</span>
        </> : null}
        confirmLabel="Delete Job"
        pending={deleteMutation.isPending}
        cancelTestId="button-delete-maint-cancel"
        confirmTestId="button-delete-maint-confirm"
        onConfirm={() => { if (deleteConfirmMaintJob) { deleteMutation.mutate(deleteConfirmMaintJob.id); setDeleteConfirmMaintJob(null); setMaintSheetOpen(false); setSelectedJob(null); } }}
      />

      <DeleteConfirmDialog
        open={deleteConfirmMaintPhoto !== null}
        onOpenChange={open => { if (!open) setDeleteConfirmMaintPhoto(null); }}
        title={`Delete this ${deleteConfirmMaintPhoto?.phase ?? ""} photo?`}
        description="This will permanently delete the photo/video from this maintenance job. This cannot be undone."
        confirmLabel="Delete Photo"
        pending={deletePhotoMutation.isPending}
        onConfirm={() => { if (deleteConfirmMaintPhoto) { deletePhotoMutation.mutate({ jobId: deleteConfirmMaintPhoto.jobId, photoId: deleteConfirmMaintPhoto.photoId }); setDeleteConfirmMaintPhoto(null); } }}
      />
    </AdminLayout>
  );
}

function MaintenanceJobActivityTimeline({ jobId }: { jobId: number }) {
  const { data: logs = [], isLoading } = useQuery<any[]>({
    queryKey: ['/api/admin/maintenance-jobs', jobId, 'audit'],
    queryFn: async () => {
      const res = await fetch(`/api/admin/maintenance-jobs/${jobId}/audit`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!jobId,
  });
  if (isLoading) return <div className="text-xs text-muted-foreground py-2">Loading activity...</div>;
  if (logs.length === 0) return null;
  return (
    <div className="mt-4 border-t pt-3">
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-2">
        <Shield className="h-3 w-3" />
        Activity Timeline
      </div>
      <div className="space-y-2">
        {logs.map((log: any) => (
          <div key={log.id} className="flex gap-2 text-xs">
            <div className="w-1 rounded-full bg-border shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <span className="font-medium">{log.actorName || "System"}</span>
              <span className="text-muted-foreground mx-1">·</span>
              <span className="capitalize">{log.action.replace(/_/g, " ")}</span>
              {log.metadata && typeof log.metadata === "object" && Object.entries(log.metadata).map(([k, v]: any) => (
                typeof v === "object" && v?.from !== undefined ? (
                  <span key={k} className="text-muted-foreground ml-1">
                    ({k.replace(/_/g, " ")}: <span className="line-through">{String(v.from ?? "—")}</span> → {String(v.to ?? "—")})
                  </span>
                ) : null
              ))}
            </div>
            <div className="text-muted-foreground whitespace-nowrap shrink-0">
              {log.createdAt ? format(new Date(log.createdAt), "MMM d, HH:mm") : ""}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
